import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteIcon from '@mui/icons-material/Delete';
import InboxIcon from '@mui/icons-material/Inbox';
import { toast } from 'react-toastify';
import { previewCurriculumHTML } from '../../utils/pdfGenerator';
import { useCurriculoActions } from '../../states/curriculo/curriculo.actions';
import { CurriculoHistoryItem } from '../../states/curriculo';

interface HistoricoCurriculosProps {
  open: boolean;
  onClose: () => void;
  contatoId: number;
  onRestoreVersion: (curriculoData: any) => void;
}

export const HistoricoCurriculos: React.FC<HistoricoCurriculosProps> = ({
  open,
  onClose,
  contatoId,
  onRestoreVersion,
}) => {
  const curriculoActions = useCurriculoActions();

  const [historicos, setHistoricos] = useState<CurriculoHistoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmRestoreOpen, setConfirmRestoreOpen] = useState<boolean>(false);
  const [selectedVersion, setSelectedVersion] = useState<CurriculoHistoryItem | null>(null);

  useEffect(() => {
    if (open && contatoId) {
      carregarHistorico();
    }
  }, [open, contatoId]);

  const carregarHistorico = async () => {
    setLoading(true);
    try {
      const data = await curriculoActions.listarHistorico(contatoId);
      setHistoricos(data);
    } catch (error) {
      toast.error('Erro ao carregar histórico de versões');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (item: CurriculoHistoryItem) => {
    try {
      toast.info('Gerando preview...');
      const htmlContent = await curriculoActions.gerarCVAtualizado(item.curriculoData);

      if (!htmlContent || typeof htmlContent !== 'string') {
        throw new Error('Formato de resposta inválido');
      }

      const nomeUsuario = item.curriculoData?.basics?.name || 'curriculo';
      previewCurriculumHTML(htmlContent, nomeUsuario);
      toast.success('Preview aberto em nova janela!');
    } catch (error) {
      toast.error('Erro ao gerar preview');
      console.error(error);
    }
  };

  const handleRestoreClick = (item: CurriculoHistoryItem) => {
    setSelectedVersion(item);
    setConfirmRestoreOpen(true);
  };

  const handleRestoreConfirm = () => {
    if (selectedVersion) {
      onRestoreVersion(selectedVersion.curriculoData);
      toast.success('Versão restaurada com sucesso!');
      setConfirmRestoreOpen(false);
      onClose();
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} às ${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 500 } },
        }}
      >
        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon color="primary" />
              <Typography variant="h6">Histórico de Versões</Typography>
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Total de versões */}
          {!loading && historicos.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Chip
                label={`${historicos.length} ${historicos.length === 1 ? 'versão salva' : 'versões salvas'}`}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Box>
          )}

          <Divider sx={{ mb: 2 }} />

          {/* Lista de versões */}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
              </Box>
            ) : historicos.length === 0 ? (
              <Box sx={{ textAlign: 'center', mt: 8 }}>
                <InboxIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  Nenhuma versão salva ainda
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Clique em "Salvar Rascunho" para criar o primeiro snapshot
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2}>
                {historicos.map((item, index) => (
                  <Card key={item.id} variant="outlined">
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          mb: 1,
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold">
                          📄 {item.descricao}
                        </Typography>
                        {index === 0 && <Chip label="Mais recente" color="success" size="small" />}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        🕐 {formatDate(item.dtCadastro)}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handlePreview(item)}
                      >
                        Ver
                      </Button>
                      <Button
                        size="small"
                        color="primary"
                        startIcon={<RestoreIcon />}
                        onClick={() => handleRestoreClick(item)}
                      >
                        Restaurar
                      </Button>
                    </CardActions>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Modal de Confirmação de Restauração */}
      <Dialog open={confirmRestoreOpen} onClose={() => setConfirmRestoreOpen(false)}>
        <DialogTitle>Restaurar esta versão?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Você deseja restaurar esta versão do currículo? As alterações não salvas serão perdidas.
          </Alert>
          {selectedVersion && (
            <Box>
              <Typography variant="body2">
                <strong>Descrição:</strong> {selectedVersion.descricao}
              </Typography>
              <Typography variant="body2">
                <strong>Data:</strong> {formatDate(selectedVersion.dtCadastro)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRestoreOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleRestoreConfirm}
            variant="contained"
            color="primary"
            startIcon={<RestoreIcon />}
          >
            Sim, Restaurar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
